import { ArrowIcon, CheckIcon, DashIcon, LockIcon } from '../../assets/icons';

interface PriceCardProps {
  badge?: string;
  name: string;
  sub: string;
  amount: string;
  terms: string;
  features: string[];
  mutedFeatures?: string[];
  bestFor?: string;
  featured?: boolean;
  /** @todo Replace "#" with the correct checkout or booking URL */
  checkoutHref: string;
  /** Custom button label; defaults to "Get Started · $amount" or "Get Started" */
  ctaLabel?: string;
  ctaGold?: boolean;
  /** When true the "Secure checkout" footer is hidden (visibility:hidden) to
   *  preserve card height alignment — use for non-checkout CTAs like scheduling */
  hideCheckout?: boolean;
  /** Optional second CTA rendered below the primary one (e.g. payment plan option) */
  secondaryCta?: { href: string; label: string };
}

export default function PriceCard({
  badge,
  name,
  sub,
  amount,
  terms,
  features,
  mutedFeatures = [],
  bestFor,
  featured = false,
  checkoutHref,
  ctaLabel,
  ctaGold = false,
  hideCheckout = false,
  secondaryCta,
}: PriceCardProps) {
  const isPrice = amount.startsWith('$');
  const resolvedLabel =
    ctaLabel || (isPrice ? `Get Started · ${amount}` : 'Get Started');
  const cardClass = `dash-price-card${featured ? ' dash-price-card--featured' : ''}`;
  const ctaClass = `dash-price-cta${ctaGold ? '' : ' dash-price-cta--dark'}`;
  const amountClass = `dash-price-amount${isPrice ? '' : ' dash-price-amount--nonprice'}`;

  return (
    <div className={cardClass}>
      {badge && <span className="dash-price-badge">{badge}</span>}

      <p className="dash-price-name font-serif">{name}</p>
      <p className="dash-price-sub">{sub}</p>

      <p className={amountClass + ' font-serif'}>{amount}</p>
      <p className="dash-price-terms">{terms}</p>

      <ul className="dash-price-features">
        {features.map((f) => (
          <li key={f}>
            <CheckIcon />
            <span>{f}</span>
          </li>
        ))}
        {mutedFeatures.map((f) => (
          <li key={f} className="dash-price-features__muted">
            <DashIcon />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {bestFor && (
        <p className="dash-price-bestfor">
          <strong>Best for:</strong> {bestFor}
        </p>
      )}

      <div className="dash-price-action">
        <a href={checkoutHref} className={ctaClass}>
          {resolvedLabel}
          <ArrowIcon />
        </a>
        {secondaryCta && (
          <a
            href={secondaryCta.href}
            className="dash-price-cta dash-price-cta--dark dash-price-cta--secondary"
          >
            {secondaryCta.label}
            <ArrowIcon />
          </a>
        )}
        <div
          className="dash-price-foot"
          aria-hidden={hideCheckout || undefined}
          style={hideCheckout ? { visibility: 'hidden' } : undefined}
        >
          <LockIcon />
          Secure checkout
        </div>
      </div>
    </div>
  );
}
